import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { DIALOG_DATA } from '../../../../global.variable';
import { AppService } from '../../../app.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { UserProfileService } from '../../../profile/user-profile/user-profile.service';
import { DeviceInfoService } from '../../services/device-info.service';
import { LoadingSpinnerService } from '../../services/loading-spinner.service';
import { ToasterService } from '../../services/toaster.service';
import { ImageCaptureComponent } from '../image-capture/image-capture.component';
import { DialogRef } from '../modal-dialogue/modal-dialogue.ref';
import { ModelDialogueService } from '../modal-dialogue/model-dialogue.service';

@Component({
  selector: 'gtapp-clock-in-out',
  standalone: false,
  templateUrl: './clock-in-out.component.html',
  styleUrl: './clock-in-out.component.scss',
})
export class ClockInOutComponent implements OnInit, OnDestroy {
  shiftClockInOutParms: any = {
    hasUserClockedIn: false,
    runsheetId: '',
    jobId: '',
    cPatrolId: '',
    shiftStartTime: new Date(),
    shiftEndTime: new Date(),
    lateClockIn: false,
    earlyClockOut: false,
    title: '',
    reason: '',
  };
  updateToStatus: any;
  offlineMode: boolean = !navigator.onLine;
  deviceInfoSubscription: Subscription = new Subscription();

  constructor(
    private dialogService: ModelDialogueService,
    private profileService: UserProfileService,
    private spinnerService: LoadingSpinnerService,
    private toasterService: ToasterService,
    private appService: AppService,
    @Optional() protected dialogRef: DialogRef,
    @Optional() @Inject(DIALOG_DATA) public dialogData: any,
    private deviceInfoService: DeviceInfoService,
  ) {}
  ngOnDestroy() {
    this.deviceInfoSubscription?.unsubscribe();
  }
  ngOnInit(): void {
    this.deviceInfoSubscription =
      this.deviceInfoService.deviceInfoSubject.subscribe((value: any) => {
        if ('appStatus' in value) {
          this.offlineMode = !value?.appStatus;
        }
      });

    this.shiftClockInOutParms = JSON.parse(
      JSON.stringify(this.dialogData?.shiftClockInOutParms || {}),
    );
    this.updateToStatus = JSON.parse(
      JSON.stringify(this.dialogData?.updateStatus || {}),
    );

    if (this.shiftClockInOutParms.hasUserClockedIn) {
      // need to clock out
      const earlyClockOut =
        new Date().getTime() <
        new Date(this.shiftClockInOutParms?.shiftEndTime).getTime();
      this.shiftClockInOutParms['earlyClockOut'] = earlyClockOut;

      this.shiftClockInOutParms['title'] = earlyClockOut
        ? 'Early Clock Out'
        : 'Clock Out';

      this.shiftClockInOutParms['message'] = earlyClockOut
        ? ''
        : 'WARNING: You will not be able to reverse this action!\n Clock out now?';
    } else {
      const lateClockIn =
        new Date().getTime() >
        new Date(this.shiftClockInOutParms?.shiftStartTime).getTime();

      this.shiftClockInOutParms['lateClockIn'] = lateClockIn;

      this.shiftClockInOutParms['title'] = lateClockIn
        ? 'Late Clock In'
        : 'Clock In';
    }
  }

  async clockInOut() {
    const isSelfieRequired =
      await this.profileService.isClockInOutSelfieRequired();

    if (isSelfieRequired) {
      // subscriber guards need to take a selfi to clock in
      const dailogRef = this.dialogService.open(ImageCaptureComponent, {
        data: { templateName: 'selfieCamera' },
      });
      dailogRef.afterClosed().subscribe((response: any) => {
        if (response) {
          if (response === 1) {
            // case where the device doesn't have camera, in that case skip it
            this.clockInOutUser();
          } else {
            this.clockInOutUser(response);
          }
        }
      });
    } else {
      this.clockInOutUser();
    }
  }
  onCloseDialog(data: any) {
    this.dialogRef.close(data);
  }

  async clockInOutUser(file?: any, responseId?: string) {
    const hasClockedIn = this.shiftClockInOutParms?.hasUserClockedIn;
    const reasonDict = this.shiftClockInOutParms;
    let fileData: FormData = new FormData();
    this.spinnerService.show(hasClockedIn ? 'Clocking Out' : 'Clocking In');
    let responseArrived = false;

    if (file) {
      setTimeout(() => {
        if (!responseArrived) {
          this.spinnerService.show('Processing Image');
        }
      }, 3000);
    }
    fileData.append('file', file);

    if (responseId) fileData.append('response_id', responseId);
    if (reasonDict) fileData.append('reason_dict', JSON.stringify(reasonDict));
    let params: any = {};
    params[hasClockedIn ? 'clock_out' : 'clock_in'] = 1;
    let url = `profile/clock_user`;
    let method: 'POST' | 'PUT' = 'POST';
    if (this?.updateToStatus?.status?.id && this.shiftClockInOutParms?.jobId) {
      fileData.append('job_status_id', this?.updateToStatus?.status?.id);
      url = `job/${this.shiftClockInOutParms?.jobId}`;
      fileData.append('id', this.shiftClockInOutParms?.jobId);
      method = 'PUT';
    }

    const response: any = await this.appService.formDataApi(
      url,
      fileData,
      false,
      params,
      method,
    );

    responseArrived = true;
    if (response?.status == 'success') {
      this.onCloseDialog(response);
      this.spinnerService.hide();

      if (file && !responseId) {
        this.toasterService.setMessage({
          successTick: true,
        });
      }
      if (response?.profile_image_set) {
        this.dialogService.open(ImageCaptureComponent, {
          data: { templateName: 'profileImageSetTemplate' },
        });
      }
    } else {
      this.spinnerService.hide();
      if (!response?.response_id) {
        this.toasterService.setMessage({
          successMessage: '',
          errorMessage: response?.message,
          timeOut: 5000,
        });
        return;
      }
      const dialogRef = this.dialogService.open(ImageCaptureComponent, {
        data: { templateName: 'faceComparisonFaledTemplate' },
      });
      const res = await firstValueFrom(dialogRef.afterClosed());

      if (res) {
        // retry to take a selfi to clock in
        const dailogRef = this.dialogService.open(ImageCaptureComponent, {
          data: { templateName: 'selfieCamera' },
        });
        const dRes = await firstValueFrom(dailogRef.afterClosed());

        if (dRes) {
          if (dRes === 1) {
            // case where the device doesn't have camera, in that case skip it
            this.clockInOutUser();
          } else {
            this.clockInOutUser(response);
          }
        }
      } else {
        this.clockInOutUser(file, response?.response_id);
      }
    }
  }
  clearAndFocusTextBox(elementId: string) {
    setTimeout(() => {
      var element = <HTMLInputElement>document.getElementById(elementId);
      element?.focus();
    }, 100);
  }
}
